import { ref } from '@vue/composition-api'

import { AuthenticationService } from '@services'

/**
 * Shared sign-in routing for the two pages that can start a login: the password
 * page and the magic-link page. Both have to answer the same questions - does
 * this environment still take passwords, does this email belong to a migrated
 * user, and is this their first time - so the answers live here rather than
 * being reimplemented either side and drifting apart.
 *
 * Nothing here grants access. Every decision is a UI routing hint; the backend
 * gates are entra_sso.guards (per environment) and the OIDC backend's
 * filter_users_by_claims (per user).
 */
export const useSsoLogin = () => {
  const checkingSso = ref(false)
  const showFirstTimePrompt = ref(false)
  const showSetPassword = ref(false)
  const nativeAuthEnabled = ref(false)
  const ssoLoginUrl = ref('')

  // Whether this environment still accepts passwords, magic links and OTP.
  // Defaults TRUE: if the config call fails or is slow, offering the form is the
  // safe failure - hiding it would leave a user with no way in at all, and the
  // backend refuses the credentials anyway if legacy login really is off.
  const legacyLoginEnabled = ref(true)

  // Whether the answer above is known yet. Callers that change what they render
  // based on it wait for this, so nothing paints on the default and then
  // rearranges. Set on failure too - an unreachable config endpoint must not
  // leave a login page blank forever.
  const configLoaded = ref(false)

  const loadAuthConfig = async () => {
    const res = await AuthenticationService.authConfig().catch(() => null)
    if (!res) {
      configLoaded.value = true
      return
    }
    const cfg = (res.data && res.data.data) || res.data || {}
    if (cfg.legacy_login_enabled === false) {
      legacyLoginEnabled.value = false
    }
    // Held whether or not passwords are accepted, so the SSO button has a
    // destination without waiting for the per-email discover call.
    if (cfg.sso_login_url && !ssoLoginUrl.value) {
      ssoLoginUrl.value = cfg.sso_login_url
    }
    configLoaded.value = true
  }

  /**
   * Resolve how this email should sign in.
   *
   * Returns true when SSO has taken over the screen (a prompt is now showing,
   * or the browser is being sent to Entra) and the caller should stop. Returns
   * false when the caller should carry on with its own legacy flow.
   */
  const resolveSsoForEmail = async (email) => {
    checkingSso.value = true
    const res = await AuthenticationService.checkSsoEnabled(email).catch(
      () => null
    )
    checkingSso.value = false

    const {
      sso_enabled,
      first_time_sso,
      never_signed_in,
      native_auth,
      sso_login_url
    } = (res && res.data && res.data.data) || {}

    if (!sso_enabled || !sso_login_url) return false

    ssoLoginUrl.value = sso_login_url
    nativeAuthEnabled.value = !!native_auth

    if (first_time_sso && never_signed_in && native_auth) {
      // Invited user creating a brand-new account: nothing to explain or
      // restore, so go straight to choosing a password.
      showSetPassword.value = true
    } else if (first_time_sso) {
      // Existing user whose sign-in method changed: explain the switch first.
      // Where the button leads depends on native auth - our own set-password
      // screen, or the hosted Entra "Forgot Password" page.
      showFirstTimePrompt.value = true
    } else {
      // Returning SSO user: straight to the hosted sign-in.
      window.location.href = sso_login_url
    }

    return true
  }

  const continueToSso = () => {
    // Native auth: stay on this page and swap in the set-password fields, so
    // the code email is LimeIQ-branded and the user never leaves the app.
    // Mounting SsoSetPassword triggers the code email (its onMounted start).
    if (nativeAuthEnabled.value) {
      showFirstTimePrompt.value = false
      showSetPassword.value = true
      return
    }
    if (ssoLoginUrl.value) window.location.href = ssoLoginUrl.value
  }

  return {
    checkingSso,
    showFirstTimePrompt,
    showSetPassword,
    nativeAuthEnabled,
    ssoLoginUrl,
    legacyLoginEnabled,
    configLoaded,

    loadAuthConfig,
    resolveSsoForEmail,
    continueToSso
  }
}
